import type { IMyTimeSummaryDay } from '@pulse/shared';
import { Box, Stack, Typography } from '@pulse/ui';

const CHART_HEIGHT = 100;

function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' });
}

// `toLocaleDateString('en-CA')` gives the local YYYY-MM-DD directly — unlike
// `toISOString().slice(0, 10)`, it never routes through UTC, so it can't roll back a day in a
// positive-offset timezone (the same class of bug fixed server-side in MeService).
function todayIso(): string {
  return new Date().toLocaleDateString('en-CA');
}

// 5 CSS bars + a dashed target line — no charting library needed for a fixed 5-bar week view.
export function WeeklyHoursChart({ days, dailyTargetHours }: { days: IMyTimeSummaryDay[]; dailyTargetHours: number }) {
  const today = todayIso();
  const maxScale = Math.max(dailyTargetHours, ...days.map((d) => d.hours), 1);
  const targetLineBottom = (dailyTargetHours / maxScale) * CHART_HEIGHT;

  return (
    <Stack spacing={1}>
      <Box sx={{ position: 'relative', height: CHART_HEIGHT, display: 'flex', alignItems: 'flex-end', gap: 1, px: 0.5 }}>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: targetLineBottom,
            borderTop: '1px dashed',
            borderColor: 'divider',
          }}
        />
        {days.map((d) => {
          const isToday = d.date === today;
          const barHeightPct = (d.hours / maxScale) * 100;
          return (
            <Stack key={d.date} alignItems="center" justifyContent="flex-end" spacing={0.5} sx={{ flex: 1, height: '100%' }}>
              {d.hours > 0 && (
                <Typography variant="caption" fontWeight={600} color={isToday ? 'secondary.main' : 'text.secondary'}>
                  {d.hours}h
                </Typography>
              )}
              <Box
                sx={{
                  width: '60%',
                  height: `${Math.max(barHeightPct, d.hours > 0 ? 3 : 0)}%`,
                  bgcolor: isToday ? 'secondary.main' : 'action.selected',
                  borderRadius: '4px 4px 0 0',
                  transition: 'height 0.2s',
                }}
              />
            </Stack>
          );
        })}
      </Box>
      <Stack direction="row" sx={{ px: 0.5 }}>
        {days.map((d) => (
          <Typography
            key={d.date}
            variant="caption"
            color={d.date === today ? 'secondary.main' : 'text.secondary'}
            fontWeight={d.date === today ? 700 : 400}
            sx={{ flex: 1, textAlign: 'center' }}
          >
            {dayLabel(d.date)}
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
}
